import type {
  Project,
  ProjectDetail,
  ProjectSummary,
} from "@shared/schema";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers || {}) },
    credentials: "same-origin",
  });
  if (res.status === 401) {
    // bubble up so the app can show the password gate
    throw new ApiError(401, "Unauthorized");
  }
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const api = {
  // auth
  authStatus: () => req<{ authenticated: boolean }>("/api/auth"),
  login: (password: string) =>
    req<{ authenticated: boolean }>("/api/auth", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // projects
  listProjects: () => req<ProjectSummary[]>("/api/projects"),
  getProject: (id: string) => req<ProjectDetail>(`/api/projects/${id}`),
  createProject: (body: Partial<Project>) =>
    req<Project>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  deleteProject: (id: string) =>
    req<{ ok: true }>(`/api/projects/${id}`, { method: "DELETE" }),

  // reference + pipeline (wired in later phases)
  ingest: (id: string, body: { type: string; value: string }) =>
    req<ProjectDetail>(`/api/projects/${id}/ingest`, { method: "POST", body: JSON.stringify(body) }),
  uploadReference: (id: string, file: File) =>
    req<ProjectDetail>(`/api/projects/${id}/upload`, {
      method: "POST",
      headers: { "content-type": file.type || "video/mp4" },
      body: file,
    }),
  analyze: (id: string) =>
    req<ProjectDetail>(`/api/projects/${id}/analyze`, { method: "POST" }),
  plan: (id: string) => req<ProjectDetail>(`/api/projects/${id}/plan`, { method: "POST" }),
  generateAll: (id: string) =>
    req<{ queued: number }>(`/api/projects/${id}/generate-all`, { method: "POST" }),
  generateVideos: (id: string) =>
    req<{ queued: number }>(`/api/projects/${id}/generate-videos`, { method: "POST" }),
  exportProject: (id: string) =>
    req<{ ok: true; downloadUrl: string }>(`/api/projects/${id}/export`, { method: "POST" }),

  // scenes
  generateFrame: (sceneId: string) =>
    req<{ ok: true }>(`/api/scenes/${sceneId}/frame`, { method: "POST" }),
  generateVideo: (sceneId: string) =>
    req<{ ok: true }>(`/api/scenes/${sceneId}/video`, { method: "POST" }),
  updateScene: (sceneId: string, patch: Record<string, unknown>) =>
    req<{ ok: true }>(`/api/scenes/${sceneId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  regenerate: (sceneId: string, body: { type: "image" | "video"; prompt?: string }) =>
    req<{ ok: true }>(`/api/scenes/${sceneId}/regenerate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
